"use client";

import React from "react";
import { Skeleton, SkeletonCard } from "./Skeleton";

export type SectionLoadingVariant = "card" | "rows" | "block";

interface SectionLoadingProps {
  /** Visual density of the placeholder. */
  variant?: SectionLoadingVariant;
  /** Number of skeleton rows when variant is "rows". */
  rows?: number;
  className?: string;
  /** Accessible label for screen readers. */
  label?: string;
}

/**
 * Section-scoped loading placeholder for shell-first pages.
 * Use inside a card/panel that owns its own query — never to block the page shell.
 */
export function SectionLoading({
  variant = "card",
  rows = 3,
  className = "",
  label = "Loading",
}: SectionLoadingProps) {
  if (variant === "card") {
    return (
      <div role="status" aria-label={label} className={className}>
        <SkeletonCard />
      </div>
    );
  }

  if (variant === "rows") {
    return (
      <div
        role="status"
        aria-label={label}
        className={`space-y-3 ${className}`}
      >
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div role="status" aria-label={label} className={`space-y-3 ${className}`}>
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}
